import React, { useState } from "react";
import "./App.css";

const TEXT = {
  en: {
    hero: {
      title: "AI-powered diet tracking for clinics and patients",
      subtitle:
        "HappyDiet brings innovation to nutrition: AI-driven, automated compliance tracking with no friction. Clinics and patients gain real-time visibility and smarter outcomes."
    },
    cta: { demo: "Book a Demo", contact: "Contact Sales" },
    journey: {
      title: "App Journey – Simple and Effective",
      steps: [
        { icon: "📸", title: "Snap a photo", text: "Patients take a picture of their meal — no manual logging." },
        { icon: "🤖", title: "AI analysis", text: "HappyDiet AI + rules engine instantly validates compliance." },
        { icon: "📲", title: "Instant feedback", text: "Patients receive immediate results and advice." },
        { icon: "👩‍⚕️", title: "Remote observability", text: "Clinics and doctors see adherence dashboards in real time." }
      ]
    },
    doctors: {
      title: "For Doctors & Clinics",
      items: [
        { icon: "🧠", title: "AI compliance", text: "Automatic dietary analysis — no manual diaries needed." },
        { icon: "📊", title: "Remote monitoring", text: "Track adherence trends per patient and cohort." },
        { icon: "🚨", title: "Deviation alerts", text: "Be notified instantly when patients go off plan." },
        { icon: "⏱️", title: "Save time", text: "Automated supervision frees valuable staff hours." }
      ]
    },
    patients: {
      title: "For Patients",
      items: [
        { icon: "✅", title: "Instant clarity", text: "Know if your meal is compliant right away." },
        { icon: "📅", title: "Daily progress", text: "Stay motivated with simple, clear daily reports." },
        { icon: "💡", title: "No friction", text: "Just snap a photo — no forms, no complexity." }
      ]
    },
    innovation: {
      title: "Innovation – AI at the Core",
      text: "HappyDiet is not just a tracking tool: it’s an AI-powered compliance engine. By combining rule-based validation and machine learning, it transforms nutrition management into a digital, scalable, and evidence-driven service for clinics and insurers."
    },
    b2b: {
      title: "For Clinics, Hospitals, and Insurers",
      items: [
        { icon: "🏥", title: "Enhance nutrition services", text: "Add a digital layer to your programs and differentiate your offering." },
        { icon: "📈", title: "Better patient outcomes", text: "Improved adherence means faster recoveries and fewer complications." },
        { icon: "💼", title: "Efficiency gains", text: "Save staff time with automated tracking and AI-powered analysis." },
        { icon: "💡", title: "New revenue opportunities", text: "Offer premium digital diet programs and wellness packages." }
      ]
    },
    vision: {
      title: "Future Vision – Evidence-based Nutrition at Scale",
      text: "By aggregating anonymized compliance data, HappyDiet provides insights for clinics, insurers, and researchers. Identify effective interventions, personalize programs at scale, reduce costs, and improve patient outcomes."
    },
    footer: `© ${new Date().getFullYear()} HappyDiet. GDPR & AI Act compliant.`
  },
  es: {
    hero: {
      title: "Seguimiento dietético con IA para clínicas y pacientes",
      subtitle:
        "HappyDiet aporta innovación a la nutrición: seguimiento automatizado con IA y sin fricción. Clínicas y pacientes obtienen visibilidad en tiempo real y mejores resultados."
    },
    cta: { demo: "Solicita una demo", contact: "Contactar ventas" },
    journey: {
      title: "El Viaje en la App – Simple y Efectivo",
      steps: [
        { icon: "📸", title: "Haz una foto", text: "El paciente captura su comida — sin registros manuales." },
        { icon: "🤖", title: "Análisis con IA", text: "La IA + motor de reglas valida la adherencia al instante." },
        { icon: "📲", title: "Feedback inmediato", text: "El paciente recibe resultados y consejos en segundos." },
        { icon: "👩‍⚕️", title: "Observabilidad remota", text: "La clínica accede a paneles de adherencia en tiempo real." }
      ]
    },
    doctors: {
      title: "Para Doctores y Clínicas",
      items: [
        { icon: "🧠", title: "Adherencia con IA", text: "Análisis automático — sin diarios manuales." },
        { icon: "📊", title: "Monitorización remota", text: "Observa tendencias de adherencia por paciente o cohortes." },
        { icon: "🚨", title: "Alertas de desviación", text: "Avisos inmediatos cuando un paciente se sale del plan." },
        { icon: "⏱️", title: "Ahorro de tiempo", text: "La supervisión automatizada libera horas del personal." }
      ]
    },
    patients: {
      title: "Para Pacientes",
      items: [
        { icon: "✅", title: "Claridad inmediata", text: "Sabrás al instante si tu comida cumple la dieta." },
        { icon: "📅", title: "Progreso diario", text: "Motivación gracias a reportes simples y claros." },
        { icon: "💡", title: "Sin fricción", text: "Solo haz una foto — sin formularios ni apps complicadas." }
      ]
    },
    innovation: {
      title: "Innovación – IA en el Núcleo",
      text: "HappyDiet no es solo un registro: es un motor de adherencia impulsado por IA. Combina reglas y aprendizaje automático para transformar la gestión nutricional en un servicio digital, escalable y basado en evidencia para clínicas y aseguradoras."
    },
    b2b: {
      title: "Para Clínicas, Hospitales y Aseguradoras",
      items: [
        { icon: "🏥", title: "Mejora tus servicios de nutrición", text: "Agrega una capa digital y diferencia tu oferta." },
        { icon: "📈", title: "Mejores resultados", text: "La adherencia mejora las recuperaciones y reduce complicaciones." },
        { icon: "💼", title: "Eficiencia operativa", text: "Ahorra tiempo con seguimiento automatizado y análisis IA." },
        { icon: "💡", title: "Nuevos ingresos", text: "Ofrece programas digitales premium de nutrición y bienestar." }
      ]
    },
    vision: {
      title: "Visión Futura – Nutrición Basada en Evidencia a Escala",
      text: "Al agregar datos de adherencia anonimizados, HappyDiet ofrece insights a clínicas, aseguradoras e investigadores. Identifica qué funciona, personaliza programas, reduce costes y mejora resultados."
    },
    footer: `© ${new Date().getFullYear()} HappyDiet. Cumple GDPR y AI Act.`
  }
};

export default function App() {
  const [lang, setLang] = useState("en");
  const t = TEXT[lang];

  return (
    <div className="App">
      <nav className="nav">
        <h1>HappyDiet</h1>
        <div>
          <button onClick={() => setLang("en")}>EN</button>
          <button onClick={() => setLang("es")}>ES</button>
        </div>
      </nav>

      <header className="hero">
        <h2>{t.hero.title}</h2>
        <p>{t.hero.subtitle}</p>
        <button>{t.cta.demo}</button>
        <button>{t.cta.contact}</button>
      </header>

      <section className="journey">
        <h3>{t.journey.title}</h3>
        <div className="grid">
          {t.journey.steps.map((step, i) => (
            <div className="card" key={i}>
              <span className="icon">{step.icon}</span>
              <h4>{step.title}</h4>
              <p>{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="benefits">
        <h3>{t.doctors.title}</h3>
        <div className="grid">
          {t.doctors.items.map((item, i) => (
            <div className="card" key={i}>
              <span className="icon">{item.icon}</span>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="benefits">
        <h3>{t.patients.title}</h3>
        <div className="grid">
          {t.patients.items.map((item, i) => (
            <div className="card" key={i}>
              <span className="icon">{item.icon}</span>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="innovation">
        <h3>{t.innovation.title}</h3>
        <p>{t.innovation.text}</p>
      </section>

      <section className="b2b">
        <h3>{t.b2b.title}</h3>
        <div className="grid">
          {t.b2b.items.map((item, i) => (
            <div className="card" key={i}>
              <span className="icon">{item.icon}</span>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="vision">
        <h3>{t.vision.title}</h3>
        <p>{t.vision.text}</p>
      </section>

      <footer>
        <p>{t.footer}</p>
      </footer>
    </div>
  );
}
